import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import api from '@/lib/api';
import { toast } from 'sonner';
import { useAuth } from '@/App';
import {
  Car,
  MapPin,
  Calendar,
  Clock,
  Users,
  Package,
  Search,
  Plus,
  Star,
  Filter,
  Loader2,
  ArrowRight
} from 'lucide-react';

export default function Carpool() {
  const { user } = useAuth();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    origin: '',
    destination: '',
    departure_date: '',
    ride_type: '',
    vehicle_type: ''
  });
  const [showFilters, setShowFilters] = useState(false);

  const fetchListings = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.origin) params.append('origin', filters.origin);
      if (filters.destination) params.append('destination', filters.destination);
      if (filters.departure_date) params.append('departure_date', filters.departure_date);
      if (filters.ride_type) params.append('ride_type', filters.ride_type);
      if (filters.vehicle_type) params.append('vehicle_type', filters.vehicle_type);

      const response = await api.get(`/carpool/listings?${params.toString()}`);
      setListings(response.data.listings || []);
    } catch (error) {
      toast.error('Failed to fetch carpool listings');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchListings();
  }, [fetchListings]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchListings();
  };

  const vehicleIcons = {
    car: '🚗',
    suv: '🚙',
    van: '🚐',
    bike: '🏍️'
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-accent to-accent/80 text-white py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-black mb-4">Carpool</h1>
              <p className="text-lg opacity-90">
                Share rides, split costs. Travel together for passengers or cargo.
              </p>
            </div>
            {user && (
              <Link to="/carpool/offer">
                <Button className="bg-white text-accent hover:bg-white/90 rounded-none gap-2" data-testid="offer-ride-btn">
                  <Plus className="w-4 h-4" />
                  Offer a Ride
                </Button>
              </Link>
            )}
          </div>

          {/* Search Form */}
          <form onSubmit={handleSearch} className="mt-8 bg-white rounded-lg p-4 shadow-lg">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <div>
                <Label className="text-gray-700 text-sm">From</Label>
                <div className="relative mt-1">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Origin city"
                    value={filters.origin}
                    onChange={(e) => setFilters(f => ({ ...f, origin: e.target.value }))}
                    className="pl-10 rounded-none text-gray-900"
                    data-testid="carpool-origin-input"
                  />
                </div>
              </div>
              <div>
                <Label className="text-gray-700 text-sm">To</Label>
                <div className="relative mt-1">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Destination city"
                    value={filters.destination}
                    onChange={(e) => setFilters(f => ({ ...f, destination: e.target.value }))}
                    className="pl-10 rounded-none text-gray-900"
                    data-testid="carpool-destination-input"
                  />
                </div>
              </div>
              <div>
                <Label className="text-gray-700 text-sm">Date</Label>
                <div className="relative mt-1">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    type="date"
                    value={filters.departure_date}
                    onChange={(e) => setFilters(f => ({ ...f, departure_date: e.target.value }))}
                    className="pl-10 rounded-none text-gray-900"
                    data-testid="carpool-date-input"
                  />
                </div>
              </div>
              <div>
                <Label className="text-gray-700 text-sm">Type</Label>
                <select
                  value={filters.ride_type}
                  onChange={(e) => setFilters(f => ({ ...f, ride_type: e.target.value }))}
                  className="w-full mt-1 h-10 px-3 border border-input bg-background rounded-none text-gray-900"
                  data-testid="carpool-type-select"
                >
                  <option value="">All types</option>
                  <option value="passenger">Passenger only</option>
                  <option value="cargo">Cargo only</option>
                  <option value="both">Both</option>
                </select>
              </div>
              <div className="flex items-end">
                <Button type="submit" className="w-full rounded-none bg-accent hover:bg-accent/90 gap-2" data-testid="carpool-search-btn">
                  <Search className="w-4 h-4" />
                  Search
                </Button>
              </div>
            </div>
          </form>
        </div>
      </div>

      {/* Results */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold">
            {loading ? 'Searching...' : `${listings.length} rides available`}
          </h2>
          <Button
            variant="outline"
            onClick={() => setShowFilters(!showFilters)}
            className="rounded-none gap-2"
          >
            <Filter className="w-4 h-4" />
            Filters
          </Button>
        </div>

        {/* Filter Panel */}
        {showFilters && (
          <div className="bg-muted/30 border border-border p-4 mb-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <Label className="text-sm">Vehicle Type</Label>
                <select
                  value={filters.vehicle_type}
                  onChange={(e) => setFilters(f => ({ ...f, vehicle_type: e.target.value }))}
                  className="w-full mt-1 h-10 px-3 border border-input bg-background rounded-none"
                >
                  <option value="">Any vehicle</option>
                  <option value="car">Car</option>
                  <option value="suv">SUV</option>
                  <option value="van">Van</option>
                  <option value="bike">Bike</option>
                </select>
              </div>
            </div>
          </div>
        )}

        {/* Listings Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-accent" />
          </div>
        ) : listings.length === 0 ? (
          <div className="text-center py-20 border border-dashed border-border">
            <Car className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
            <h3 className="text-lg font-bold mb-2">No rides found</h3>
            <p className="text-muted-foreground mb-4">Try adjusting your search criteria</p>
            {user && (
              <Link to="/carpool/offer">
                <Button className="rounded-none bg-accent hover:bg-accent/90">
                  Offer a Ride
                </Button>
              </Link>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {listings.map((listing) => (
              <CarpoolCard key={listing.id} listing={listing} vehicleIcons={vehicleIcons} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function CarpoolCard({ listing, vehicleIcons }) {
  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-IN', {
      weekday: 'short',
      day: 'numeric',
      month: 'short'
    });
  };

  return (
    <Link to={`/carpool/${listing.id}`} data-testid={`carpool-listing-${listing.id}`}>
      <div className="border border-border p-4 hover:border-accent transition-colors cursor-pointer">
        <div className="flex items-start justify-between gap-4">
          {/* Route Info */}
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-3">
              <div className="text-2xl">{vehicleIcons[listing.vehicle_type] || '🚗'}</div>
              <div>
                <p className="font-bold">{listing.driver_name}</p>
                <p className="text-sm text-muted-foreground">
                  {listing.vehicle_name || listing.vehicle_type.toUpperCase()}
                  {listing.vehicle_number && ` • ${listing.vehicle_number}`}
                </p>
              </div>
              {listing.rating > 0 && (
                <div className="flex items-center gap-1 ml-auto">
                  <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                  <span className="font-medium">{listing.rating.toFixed(1)}</span>
                  <span className="text-sm text-muted-foreground">({listing.total_reviews})</span>
                </div>
              )}
            </div>

            <div className="flex items-center gap-2 text-lg">
              <span className="font-medium">{listing.origin}</span>
              <ArrowRight className="w-4 h-4 text-muted-foreground" />
              <span className="font-medium">{listing.destination}</span>
            </div>

            {listing.stops?.length > 0 && (
              <p className="text-sm text-muted-foreground mt-1">
                via {listing.stops.join(', ')}
              </p>
            )}

            <div className="flex items-center gap-4 mt-3 text-sm text-muted-foreground">
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {formatDate(listing.departure_date)}
              </span>
              <span className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {listing.departure_time}
              </span>
            </div>

            {/* Amenities */}
            {listing.amenities?.length > 0 && (
              <div className="flex gap-2 mt-3">
                {listing.amenities.map((amenity) => (
                  <span
                    key={amenity}
                    className="px-2 py-0.5 bg-muted text-xs uppercase"
                  >
                    {amenity.replace('_', ' ')}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Price & Availability */}
          <div className="text-right">
            {listing.seats_remaining > 0 && (
              <div className="mb-2">
                <p className="text-2xl font-black text-accent">₹{listing.price_per_seat}</p>
                <p className="text-sm text-muted-foreground">per seat</p>
                <p className="text-sm flex items-center justify-end gap-1 mt-1">
                  <Users className="w-4 h-4" />
                  {listing.seats_remaining} seats left
                </p>
              </div>
            )}

            {listing.cargo_space_available && listing.cargo_remaining_kg > 0 && (
              <div className="mt-2 pt-2 border-t border-border">
                <p className="text-lg font-bold">₹{listing.price_per_kg}/kg</p>
                <p className="text-sm flex items-center justify-end gap-1">
                  <Package className="w-4 h-4" />
                  {listing.cargo_remaining_kg}kg available
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </Link>
  );
}
